/*eslint-disable*/
import React, { useState } from 'react';
import styled from 'styled-components';
import { CustomCheckBox } from 'pg-ui-library';

interface IMultiSelectDropdownOption {
  key: string;
  label: string;
  value: string;
}

interface IMultiSelectDropdownProps {
  id: string;
  name: string;
  label: string;
  options: IMultiSelectDropdownOption[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  disabled?: boolean;
}

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 8px 16px;
  border: 1px solid #dbdbdc;
  border-radius: 5px;
  background: white;
  cursor: pointer;
  text-align: left;
  font-size: 1.6rem;
  color: #222247;
  min-height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:disabled {
    background: #f3f4f8;
    border: none;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    border-color: #4948ac;
  }
`;

const DropdownArrow = styled.span<{ isOpen: boolean }>`
  transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
  font-size: 12px;
`;

const DropdownList = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dbdbdc;
  border-top: none;
  border-radius: 0 0 5px 5px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  display: ${props => (props.isOpen ? 'block' : 'none')};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const DropdownItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background-color: #f3f4f8;
  }
`;

const CheckboxContainer = styled.div`
  margin-right: 8px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-size: 1.6rem;
  color: #222247;
`;

const SelectedText = styled.span`
  color: #222247;
  font-size: 1.6rem;
`;

export const MultiSelectDropdown: React.FC<IMultiSelectDropdownProps> = ({
  id,
  name,
  label,
  options,
  selectedValues,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (value: string) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newSelectedValues);
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return 'Select options...';
    }
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0]);
      return option ? option.label : selectedValues[0];
    }
    return `${selectedValues.length} options selected`;
  };

  return (
    <DropdownContainer>
      <Label htmlFor={id}>{label}</Label>
      <DropdownButton type="button" onClick={handleToggle} disabled={disabled} id={id} name={name}>
        <SelectedText>{getDisplayText()}</SelectedText>
        <DropdownArrow isOpen={isOpen}>▼</DropdownArrow>
      </DropdownButton>
      <DropdownList isOpen={isOpen}>
        {options.map(option => (
          <DropdownItem key={option.key} onClick={() => handleOptionClick(option.value)}>
            <CheckboxContainer>
              <CustomCheckBox
                id={`${id}-${option.value}`}
                label=""
                checked={selectedValues.includes(option.value)}
                readOnly
              />
            </CheckboxContainer>
            {option.label}
          </DropdownItem>
        ))}
      </DropdownList>
    </DropdownContainer>
  );
};
